import { currentBrandCountriesAndLang, defaultBrandLang, chooseBrandLang } from '@b2ag/app-brands/src/country-lang'
import { currentAppBrand } from '@b2ag/app-brands'
import type {
  RouteLocationResolved,
  RouteLocationNormalizedLoaded,
  RouteLocationAsString,
  RouteLocationAsRelative,
  RouteLocationAsPath,
  RouteLocationNormalized,
} from 'vue-router'
import { getWindowLocationInfos, setWindowLocationHref } from './utils'

const ROUTE_PATHS_TO_LOCALIZE = ['article-page', 'product', 'category']

function splitPathInCountryAndLang(path: string) {
  const cleanPath = path.split(/[?#]/)[0]!
  const splitPath = cleanPath.split('/')
  const country = splitPath[1]
  const lang = splitPath[2]
  return { country, lang }
}

export function identifyCountryWithPath(path: string) {
  const countryList = currentBrandCountriesAndLang()
    .map((countryAndLang) => countryAndLang.country)
    .filter((value, index, array) => array.indexOf(value) === index)

  const identifiedCountry = splitPathInCountryAndLang(path).country

  if (!identifiedCountry || !countryList.includes(identifiedCountry)) return

  return identifiedCountry
}

export function identifyLangWithPath(path: string) {
  const identifiedCountry = identifyCountryWithPath(path)
  if (!identifiedCountry) return

  const langList = currentBrandCountriesAndLang()
    .filter((countryAndLang) => countryAndLang.country === identifiedCountry)
    .map((countryAndLang) => countryAndLang.lang)

  const identifiedLang = splitPathInCountryAndLang(path).lang

  if (!identifiedLang || !langList.includes(identifiedLang)) return

  return identifiedLang
}

export function getI18nBaseParams(path: string) {
  const { country: defaultCountry, lang: defaultLang } = currentBrandCountriesAndLang()[0]!
  const countryInPath = identifyCountryWithPath(path)
  const langInPath = identifyLangWithPath(path)

  if (countryInPath) {
    if (langInPath && countryInPath === defaultCountry && langInPath !== defaultLang)
      return {
        base: `/${countryInPath}/${langInPath}`,
      }

    if (countryInPath !== defaultCountry) {
      if (langInPath)
        return {
          base: `/${countryInPath}/${langInPath}`,
        }

      const defaultLangForCountry = currentBrandCountriesAndLang().find((cl) => cl.country === countryInPath)!.lang
      return {
        base: `/${countryInPath}/${defaultLangForCountry}`,
        redirection: {
          from: `/${countryInPath}`,
          to: `/${countryInPath}/${defaultLangForCountry}`,
        },
      }
    }
  }

  const doRedirectToRoot = countryInPath === defaultCountry

  return {
    base: '/',
    redirection: doRedirectToRoot
      ? {
          from: `/${countryInPath}${langInPath ? '/' + langInPath : ''}`,
          to: '/',
        }
      : undefined,
  }
}

export function getLocaleForI18n(path: string) {
  const country = identifyCountryWithPath(path)

  if (country && path !== '/') {
    let lang = identifyLangWithPath(path)

    if (!lang)
      lang = currentBrandCountriesAndLang().filter((countryAndLang) => countryAndLang.country === country)[0]!.lang

    return `${lang}-${country?.toUpperCase()}`
  }
  return defaultBrandLang()
}

export function checkAndRedirectIfUserIsNotOnCorrectLocalizedRoute(
  localesAvailablesForUser?: string[],
  showError?: (error: { statusCode: number; statusMessage: string; fatal?: boolean; data?: any }) => void,
) {
  if (
    !localesAvailablesForUser ||
    localesAvailablesForUser.length === 0 ||
    localesAvailablesForUser?.includes(getLocaleForI18n(getWindowLocationInfos().pathname)!)
  )
    return

  if (showError && !chooseBrandLang(currentAppBrand).includes(localesAvailablesForUser[0]!))
    throw showError({
      statusCode: 400,
      statusMessage: `The locale "${localesAvailablesForUser[0]}" set for your account is not available for the current brand "${currentAppBrand}". Please contact the support.`,
      data: { layout: 'minimal' },
      fatal: true,
    })

  const currentBasePath = getI18nBaseParams(getWindowLocationInfos().pathname).base
  const basePath = identifyPathWithLocale(localesAvailablesForUser[0]!)
  const currentOrigin = getWindowLocationInfos().origin
  const routerBasePathToDelete = currentBasePath === '/' ? undefined : currentBasePath

  let currentPathname = getWindowLocationInfos().pathname

  // Supprimer le basePath en utilisant replace au lieu de split pour éviter les problèmes avec plusieurs occurrences
  if (routerBasePathToDelete) {
    currentPathname = currentPathname.replace(routerBasePathToDelete, '')
  }

  // Construire la nouvelle URL et nettoyer les doubles slashes
  let newUrl = `${currentOrigin}${basePath}${currentPathname}`.replace(/\/+/g, '/')
  // Rétablir le protocole qui a été affecté par le replace
  newUrl = newUrl.replace(':/', '://')

  // Préserver les query params et le hash lors de la redirection
  const currentSearch = getWindowLocationInfos().search
  const currentHash = getWindowLocationInfos().hash
  newUrl = newUrl + currentSearch + currentHash

  // Protection anti-boucle : ne rediriger que si l'URL est différente
  const currentUrl = getWindowLocationInfos().href
  if (newUrl === currentUrl) {
    return
  }

  setWindowLocationHref(newUrl)
}

export function identifyPathWithLocale(locale: string) {
  const country = locale.slice(3).toLowerCase()
  const lang = locale.slice(0, 2)
  return `/${country}/${lang}`
}

/**
 * Removes locale prefix from path (e.g., /ca/fr/category/... -> /category/...)
 * Handles both /{country}/{lang} and /{country} patterns
 * Only removes if the prefix matches a valid locale configuration
 * @param path - The path with potential locale prefix
 * @returns The path without locale prefix
 */
export function removeLocaleFromPath(path: string): string {
  const identifiedCountry = identifyCountryWithPath(path)
  const identifiedLang = identifyLangWithPath(path)

  // If we have both country and lang, remove /{country}/{lang}
  if (identifiedCountry && identifiedLang) {
    return path.replace(`/${identifiedCountry}/${identifiedLang}`, '')
  }

  // If we only have country (valid), remove /{country}
  if (identifiedCountry) {
    return path.replace(`/${identifiedCountry}`, '')
  }

  // No valid locale prefix found, return path as-is
  return path
}

export function getAliasForRouter(currentBrand: string, path: string = '/', alias: string[] = []): string[] {
  const langList = chooseBrandLang(currentBrand)
  const aliasList: string[] = []

  for (const locale of langList) {
    const country = locale.slice(3).toLowerCase()
    const lang = locale.slice(0, 2)

    ;[path].concat(alias).forEach((currentPath: string) => {
      // Add country variant
      aliasList.push(`/${country}${currentPath}`)
      // Add country/lang variant if it's not default US-EN
      aliasList.push(`/${country}/${lang}${currentPath}`)
    })
  }

  // Remove duplicates
  return [...new Set(alias.concat(aliasList))]
}

export function localizeRoute(
  to: Record<string, any> | string,
  path: string,
  routerResolve: (
    to: RouteLocationAsString | RouteLocationAsRelative | RouteLocationAsPath,
    currentLocation?: RouteLocationNormalizedLoaded,
  ) => RouteLocationResolved,
): Record<string, any> | string {
  const { base } = getI18nBaseParams(path)
  let finalTo: Record<string, any> | string = {
    fullPath: (base + (to ? routerResolve(to).fullPath : '')).replace(/\/\//g, '/'),
  }

  // If the route is found, we use the resolved route with basePath
  if (routerResolve(finalTo.fullPath).name !== 'not-found') {
    if (typeof to === 'object')
      finalTo = {
        ...to,
        ...finalTo,
      }

    finalTo = {
      ...finalTo,
      name: undefined,
      path: finalTo.fullPath,
      href: finalTo.fullPath,
      query: Object.fromEntries(new URLSearchParams(finalTo.fullPath.split('?')[1]).entries()),
    }
  }
  // If we dont't have a known route, we use the original params
  else finalTo = to

  return finalTo
}

export function redirectToWithLocal(locale: string, path: string) {
  const localizedPath = identifyPathWithLocale(locale)
  setWindowLocationHref(getWindowLocationInfos().origin + localizedPath + removeLocaleFromPath(path))
}

export function redirectToAlternateWithLocale(locale: string, path: string) {
  // Try to find the alternate link for the requested locale
  const alternateLinks = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'))
  const targetLink = alternateLinks.find((link) => link.getAttribute('hreflang') === locale)
  if (targetLink && targetLink.getAttribute('href') && typeof targetLink.getAttribute('href') === 'string') {
    // If we found a matching alternate link, use its href
    setWindowLocationHref(targetLink.getAttribute('href') as string)
    return
  }

  // redirect to default behavior if no matching alternate link is found
  redirectToWithLocal(locale, path)
}

/**
 * Gets the current route name from a route object
 * @param currentRoute - The current route object
 * @returns The name of the current route as a string
 */
export function getCurrentRouteName(currentRoute: RouteLocationNormalized): string {
  if (currentRoute.name) {
    return currentRoute.name.toString()
  }
  if (currentRoute.matched?.[0]?.name) {
    return currentRoute.matched[0].name.toString()
  }
  return ''
}

/**
 * Handles locale change based on the current route
 * @param newLocale - The new locale to change to
 * @param routePath - The current route path
 * @param routeName - The current route name
 * @param routeNamesToLocalize - Array of route names that should use alternate localization
 */
export function handleLocaleChange(
  newLocale: string,
  routePath: string,
  routeName: string,
  routeNamesToLocalize: string[] = ROUTE_PATHS_TO_LOCALIZE,
) {
  if (routeNamesToLocalize.includes(routeName)) {
    redirectToAlternateWithLocale(newLocale, routePath)
  } else {
    redirectToWithLocal(newLocale, routePath)
  }
}
